import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';
import { useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { RatesHistoryAnalyticsSurface } from '~components/PricingHistory/PricingHistoryButton';
import PricingHistoryPanel from '~components/PricingHistory/PricingHistoryPanel';
import ProjectTimeline from '~components/Project/ProjectTimeline';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { Project } from '~hooks/useProjects/models';

interface ProjectPricingHistoryButtonProps {
  project: Project;
  /**
   * When set, render a labeled button (for modal headers, following the modal-header pattern)
   * instead of the icon-only trigger used in the project row.
   */
  label?: string;
  analyticsSurface: RatesHistoryAnalyticsSurface;
}

/**
 * Trigger that opens the history panel for a Project. Unlike the job/order/load triggers (which
 * read `pricing_events` and are labeled "Rates History"), a project's history is the existing
 * ProjectTimeline (ProjectEvent data — rate cards, material/tax changes, creation, state), so it
 * is labeled "Timeline" and is not flag-gated.
 */
export default function ProjectPricingHistoryButton({
  project,
  label,
  analyticsSurface,
}: ProjectPricingHistoryButtonProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    AnalyticsController.track('rates_history_viewed', {
      scope: 'project',
      surface: analyticsSurface,
    });
    setIsPanelOpen(true);
  };

  return (
    <>
      {label ? (
        <SmallButton
          color="brandV2OrangeDark"
          onClick={handleOpen}
          startIcon={<HistoryOutlined sx={{ fontSize: '18px' }} />}
          variant="text"
        >
          {label}
        </SmallButton>
      ) : (
        <Tooltip title={t('project.timeline.title')}>
          <IconButton
            aria-label={t('project.timeline.title')}
            onClick={handleOpen}
            size="small"
          >
            <HistoryOutlined
              sx={(theme) => ({
                color: theme.brandV2.colors.treadOrangeDark,
                fontSize: '18px',
              })}
            />
          </IconButton>
        </Tooltip>
      )}

      <PricingHistoryPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        subtitle={t('loads.pricing.history.project_ref', { ref: project.name })}
        title={t('project.timeline.title')}
      >
        <ProjectTimeline project={project} />
      </PricingHistoryPanel>
    </>
  );
}
